import { useQuery } from "@tanstack/react-query";
import { getWidgetActionsApiClient } from "@shared/apis/sidekick/api-factories";
import { SinglePropertyResponse } from "@shared/apis/sidekick/generated";
import { extractQueryState } from "../helpers/extract-query-state";
import {
  ApiError,
  apiResponseErrorHandler,
} from "@shared/apis/sidekick/api-error-handler";

const WIDGETS_FIND_LISTINGS_QUERY_KEY = "widgets-get-listing-by-mls-id";
const CACHE_TIME_IN_MS = 1000 * 60 * 5; // 5 minutes

interface Params {
  mlsId: string;
  mlsSource: string;
}

export const buildWidgetsGetListingByMlsIdQueryKey = ({
  mlsId,
  mlsSource,
}: Params) => {
  return [WIDGETS_FIND_LISTINGS_QUERY_KEY, { mlsId, mlsSource }];
};

export function useWidgetsGetListingByMlsId(props: {
  mlsId: string;
  mlsSource: string;
  onSuccess?: (data: SinglePropertyResponse) => void;
  onError?: (error: ApiError) => void;
}) {
  const { mlsId, mlsSource, onSuccess, onError } = props;
  const api = getWidgetActionsApiClient();

  const query = useQuery({
    queryKey: buildWidgetsGetListingByMlsIdQueryKey({ mlsId, mlsSource }),
    queryFn: async () => {
      try {
        const response =
          await api.widgetsControllersSidekickGetListingDetailsByMlsId({
            getListingDetailsByMlsIdAv8Request: {
              operationName: "GetListingDetailsByMlsId",
              listingReference: {
                mlsId,
                mlsSource,
              },
            },
          });
        onSuccess?.(response);
        return response;
      } catch (error) {
        const apiError = await apiResponseErrorHandler(error);
        onError?.(apiError);
        throw apiError;
      }
    },
    enabled: !!mlsId && !!mlsSource,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = (error as any)?.response?.status || 0;
      if (failureCount < 3 && status === 429) {
        return true;
      }
      return false;
    },
    gcTime: CACHE_TIME_IN_MS,
    staleTime: CACHE_TIME_IN_MS,
  });

  return extractQueryState(query);
}
